<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { eventSchema, type EventFormValues } from './event-schema'
import BBtn from '@lib/components/base/BBtn.vue'
import BInput from '@lib/components/base/BInput.vue'
import BDateTimeInput from '@lib/components/base/BDateTimeInput.vue'
import { sdk } from '@/graphql/client'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import type { Event } from '@/composables/useEventsData'

const props = defineProps<{ event: Event | null }>()
const emit = defineEmits<{ saved: []; deleted: []; cancel: [] }>()

const { t } = useI18n()
const toast = useToast()
const { confirm } = useConfirm()

const isCreate = computed(() => props.event === null)
const canDelete = computed(() => !isCreate.value && (props.event!.bzucoId ?? 0) === 0)

function initialValues(): EventFormValues {
    return {
        name: props.event?.name ?? '',
        startAt: props.event?.startAt ?? null,
        endAt: props.event?.endAt ?? null
    }
}

const { handleSubmit, defineField, errors, resetForm, meta, values } = useForm<EventFormValues>({
    validationSchema: toTypedSchema(eventSchema),
    initialValues: initialValues()
})

const [name] = defineField('name')
const [startAt] = defineField('startAt')
const [endAt] = defineField('endAt')

const submitting = ref(false)
const deleting = ref(false)

// Visual-only warning when creating with a past startAt. Not a validation error.
const startInPast = computed<boolean>(() => {
    if (!isCreate.value) return false
    const s = values.startAt
    if (!s) return false
    const ts = new Date(s).getTime()
    return !Number.isNaN(ts) && ts < Date.now()
})

const onSubmit = handleSubmit(async vals => {
    submitting.value = true
    try {
        if (isCreate.value) {
            const r = await sdk.AdminCreateEvent({
                name: vals.name,
                startAt: vals.startAt,
                endAt: vals.endAt
            })
            if (r.admin?.event?.create?.ok) {
                toast.success(t('events.toast.saved'))
                resetForm()
                emit('saved')
            } else {
                toast.error(t('common.errors.generic'))
            }
        } else {
            const r = await sdk.AdminUpdateEvent({
                id: props.event!.id,
                name: vals.name,
                startAt: vals.startAt,
                endAt: vals.endAt
            })
            if (r.admin?.event?.update?.ok) {
                toast.success(t('events.toast.saved'))
                resetForm({ values: vals })
                emit('saved')
            } else {
                toast.error(t('common.errors.generic'))
            }
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        submitting.value = false
    }
})

async function onDelete() {
    if (!props.event) return
    const ok = await confirm({
        title: t('events.confirm.deleteTitle'),
        message: t('events.confirm.deleteBody', { name: props.event.name }),
        severity: 'danger'
    })
    if (!ok) return
    deleting.value = true
    try {
        const r = await sdk.AdminDeleteEvent({ id: props.event.id })
        if (r.admin?.event?.delete?.ok) {
            toast.success(t('events.toast.deleted'))
            emit('deleted')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        deleting.value = false
    }
}

async function onCancel() {
    if (meta.value.dirty) {
        const ok = await confirm({
            title: t('events.confirm.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    emit('cancel')
}

// Re-init form values when the event prop changes (e.g., different row expanded)
watch(
    () => props.event?.id,
    () => {
        resetForm({ values: initialValues() })
    }
)

defineExpose({ isDirty: () => meta.value.dirty })
</script>

<template>
    <form class="space-y-4" @submit.prevent="onSubmit">
        <label class="block">
            <span class="text-xs font-semibold text-gray-600 mb-1 block">
                {{ t('events.form.name') }} *
            </span>
            <BInput v-model="name" :invalid="!!errors.name" />
            <span v-if="errors.name" class="text-xs text-red mt-1 block">{{ errors.name }}</span>
        </label>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{ t('events.form.startAt') }}</span>
                <BDateTimeInput v-model="startAt" />
                <span v-if="startInPast" class="text-xs text-yellow mt-1 block">
                    {{ t('events.form.warnings.startInPast') }}
                </span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{ t('events.form.endAt') }}</span>
                <BDateTimeInput v-model="endAt" :invalid="!!errors.endAt" />
                <span v-if="errors.endAt" class="text-xs text-red mt-1 block">{{ errors.endAt }}</span>
            </label>
        </div>

        <div class="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
            <div>
                <BBtn
                    v-if="!isCreate"
                    severity="danger"
                    size="small"
                    :disabled="!canDelete || deleting"
                    :title="canDelete ? '' : t('events.bzucoSynced.tooltip')"
                    type="button"
                    @click="onDelete"
                >
                    {{ t('common.actions.delete') }}
                </BBtn>
            </div>
            <div class="flex gap-2">
                <BBtn severity="secondary" size="small" type="button" @click="onCancel">
                    {{ t('common.actions.cancel') }}
                </BBtn>
                <BBtn size="small" type="submit" :disabled="!meta.valid || !meta.dirty || submitting">
                    {{ t('common.actions.save') }}
                </BBtn>
            </div>
        </div>
    </form>
</template>
